import { type Spec } from "@json-render/react";
import { jsonrepair } from "jsonrepair";
import { isEdgenChartComponentType } from "../Charts/edgen-charts/chartComponentTypes";

interface DynamicUiItemRenderContext {
  collapsed?: boolean;
  notificationId?: string;
  metrics?: string;
  fallbackText?: string;
}

interface DynamicUiSpecItem {
  kind: "spec";
  spec: Spec;
  renderContext: DynamicUiItemRenderContext;
}

interface DynamicUiChartItem {
  kind: "chart";
  spec: Spec;
  renderContext: DynamicUiItemRenderContext;
}

export type DynamicUiItem = DynamicUiSpecItem | DynamicUiChartItem;

interface DynamicUiParseResult {
  markdown: string;
  items: DynamicUiItem[];
  hasPendingDynamicUi: boolean;
}

const OPEN_TAG = "<dynamic_ui";
const CLOSE_TAG = "</dynamic_ui>";

/**
 * Parse JSON with automatic repair for common LLM output issues
 * (missing closing brackets, trailing commas, etc.)
 */
const tryParseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    const repaired = jsonrepair(raw);
    return JSON.parse(repaired);
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isValidSpec = (value: unknown): value is Spec => {
  if (!isRecord(value)) {
    return false;
  }

  const { root, elements } = value;

  return (
    typeof root === "string" &&
    root.length > 0 &&
    isRecord(elements) &&
    isRecord(elements[root])
  );
};

const getRootElement = (spec: Spec): Record<string, unknown> | undefined => {
  const elements = spec.elements as Record<string, unknown>;
  const rootElement = elements[spec.root];

  return isRecord(rootElement) ? rootElement : undefined;
};

const isChartSpec = (spec: Spec): boolean =>
  isEdgenChartComponentType(getRootElement(spec)?.type);

const parseBooleanAttribute = (
  value: string | undefined,
): boolean | undefined => {
  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  return undefined;
};

const parseStringAttribute = (
  openingTag: string,
  attributeName: string,
): string | undefined => {
  const escapedAttributeName = attributeName.replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&",
  );
  const attributeRegex = new RegExp(
    `\\b${escapedAttributeName}\\s*=\\s*(['"])([\\s\\S]*?)\\1`,
  );
  const value = openingTag.match(attributeRegex)?.[2]?.trim();

  return value ? value : undefined;
};

const parseDynamicUiOpeningTag = (
  openingTag: string,
): DynamicUiItemRenderContext => {
  const collapsed = parseBooleanAttribute(
    parseStringAttribute(openingTag, "collapsed"),
  );
  const notificationId = parseStringAttribute(openingTag, "id");
  const metrics = parseStringAttribute(openingTag, "metrics");
  const fallbackText = parseStringAttribute(openingTag, "fallback");

  if (
    collapsed === undefined &&
    !notificationId &&
    metrics === undefined &&
    !fallbackText
  ) {
    return {};
  }

  return {
    ...(collapsed === undefined ? {} : { collapsed }),
    ...(notificationId ? { notificationId } : {}),
    ...(metrics === undefined ? {} : { metrics }),
    ...(fallbackText ? { fallbackText } : {}),
  };
};

const isValidOpenTagBoundary = (char: string | undefined): boolean =>
  char === undefined || char === ">" || /\s/.test(char);

const findNextOpenTag = (
  content: string,
  cursor: number,
): {
  openIndex: number;
  openTagEndIndex: number;
  openingTag: string | null;
} | null => {
  let searchIndex = cursor;

  while (searchIndex < content.length) {
    const openIndex = content.indexOf(OPEN_TAG, searchIndex);

    if (openIndex === -1) {
      return null;
    }

    const boundaryChar = content[openIndex + OPEN_TAG.length];

    if (!isValidOpenTagBoundary(boundaryChar)) {
      searchIndex = openIndex + OPEN_TAG.length;
      continue;
    }

    const openTagEndIndex = content.indexOf(">", openIndex + OPEN_TAG.length);

    if (openTagEndIndex === -1) {
      return {
        openIndex,
        openTagEndIndex,
        openingTag: null,
      };
    }

    return {
      openIndex,
      openTagEndIndex,
      openingTag: content.slice(openIndex, openTagEndIndex + 1),
    };
  }

  return null;
};

export const parseDynamicUiBlocks = (content: string): DynamicUiParseResult => {
  const items: DynamicUiItem[] = [];
  let markdown = "";
  let cursor = 0;
  let hasPendingDynamicUi = false;

  while (cursor < content.length) {
    const openTag = findNextOpenTag(content, cursor);

    if (!openTag) {
      markdown += content.slice(cursor);
      break;
    }

    markdown += content.slice(cursor, openTag.openIndex);

    if (openTag.openTagEndIndex === -1 || !openTag.openingTag) {
      hasPendingDynamicUi = true;
      markdown += `\n<chart-loading></chart-loading>\n`;
      break;
    }

    const jsonStart = openTag.openTagEndIndex + 1;
    const closeIndex = content.indexOf(CLOSE_TAG, jsonStart);

    if (closeIndex === -1) {
      hasPendingDynamicUi = true;
      markdown += `\n<chart-loading></chart-loading>\n`;
      break;
    }

    const rawJson = content.slice(jsonStart, closeIndex).trim();
    const renderContext = parseDynamicUiOpeningTag(openTag.openingTag);

    try {
      const parsed = tryParseJson(rawJson);

      if (parsed && isValidSpec(parsed)) {
        const index = items.length;
        const kind = isChartSpec(parsed) ? "chart" : "spec";
        items.push({
          kind,
          spec: parsed,
          renderContext,
        });
        markdown +=
          kind === "chart"
            ? `\n<edgen-chart-render data-index="${index}"></edgen-chart-render>\n`
            : `\n<chat-json-render data-index="${index}"></chat-json-render>\n`;
      } else if (renderContext.fallbackText) {
        markdown += renderContext.fallbackText;
      }
    } catch {
      if (renderContext.fallbackText) {
        markdown += renderContext.fallbackText;
      }
    }

    cursor = closeIndex + CLOSE_TAG.length;
  }

  return {
    markdown,
    items,
    hasPendingDynamicUi,
  };
};
